import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import { ApiError } from "@/src/libs/ApiError";

/**
 * Taille de la fenêtre demandée de chaque côté du message signalé.
 *
 * Dix : assez pour lire l'enchaînement, assez peu pour que la fenêtre ne
 * devienne pas une lecture du fil entier. Le serveur plafonne à 50 et renvoie
 * `hasMoreBefore` / `hasMoreAfter`, donc ce qui manque se voit — il n'est pas
 * tronqué en silence.
 */
const WINDOW = 10;

/**
 * La conversation autour d'un message signalé.
 *
 * ⚠️ **`isEnabled` n'est pas une optimisation.** La requête ne doit partir
 * qu'à l'ouverture de la fenêtre : la déclencher au rendu de la liste irait
 * chercher le contexte des dix cartes de l'écran, dont neuf que personne ne
 * regardera — et ferait consulter à la modération des conversations qu'elle
 * n'a pas ouvertes.
 */
export function useReportContext(reportId: number, isEnabled: boolean) {
  const $api = useApiClient();
  const t = useTranslations("reports");

  const { data, error, isError, isLoading } = $api.useQuery(
    "get",
    "/api/v1/moderation/reports/{id}/context",
    {
      params: {
        path: { id: reportId },
        query: { before: WINDOW, after: WINDOW },
      },
    },
    { enabled: isEnabled },
  );

  /**
   * Un 404 est un **cas normal** : le signalement ne vise pas un message, ou le
   * message a disparu de la base (`REPORT_CONTEXT_UNAVAILABLE`). La fenêtre le
   * dit elle-même ; un toast d'erreur ferait croire à un incident. Tout le
   * reste — 403, 500, réseau — mérite bien un toast.
   */
  const isMissing = ApiError.from(error)?.status === 404;

  useQueryErrorToast(isError, t("context-error"), error, !isMissing);

  return {
    context: data?.data,
    isLoading,
    isError,
    isMissing,
  };
}
